import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { NgIf } from '@angular/common';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState } from '@ngrx/signals';
import { YearListComponent } from '../year-list/year-list.component';
import { DataLayerTypeIconComponent } from '../../atoms/data-layer-type-icon/data-layer-type-icon.component';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

@Component({
  selector: 'app-data-layer-type-entry',
  standalone: true,
  imports: [
    NgIf,
    AlertComponent,
    StyleClassModule,
    MenuModule,
    ButtonModule,
    YearListComponent,
    DataLayerTypeIconComponent,
    DebugComponent,
  ],
  templateUrl: './data-layer-type-entry.component.html',
  styleUrl: './data-layer-type-entry.component.scss',
})
export class DataLayerTypeEntryComponent implements OnInit {
  globalStore = inject(GlobalsStore);
  activeUserStore = inject(ActiveUserStore);
  dataLayerStore = inject(DataLayerStore);
  menuItems: MenuItem[] = [];
  initialDataLayerId: string | undefined = undefined;
  routerLink!: any;

  @Input() selectionMode: 'global' | 'multiple' = 'global';
  @Input() showLabel = true;
  @Input() dataLayerType!: DataLayerType;
  @Input() showControls = true;
  @Output() addYear = new EventEmitter<DataLayerType>();

  constructor() {
    effect(() => {
      const globalState = getState(this.globalStore);

      if (
        globalState.selectedLayer &&
        globalState.selectedLayer._id !== this.initialDataLayerId
      ) {
        this.initialize();
      }
    });
  }

  /**
   * Angular lifecycle hook that is called after the component's data-bound properties are initialized.
   * It is used to perform component initialization logic.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.initialize();
  }

  /**
   * Initializes the necessary data for the component by setting the initial data layer ID,
   * determining the router link, and retrieving the menu items based on the router link.
   *
   * @return {void} No return value.
   */
  initialize(): void {
    this.initialDataLayerId = this.getInitialDataLayerId();
    this.routerLink = this.getRouterLink();
    this.menuItems = this.getMenuItems(this.routerLink);
  }

  /**
   * Retrieves the initial data layer ID based on the currently selected layer or the latest data layer under the specified parent type.
   *
   * @return {string|undefined} The ID of the initial data layer or undefined if no layer matches the criteria.
   */
  getInitialDataLayerId() {
    const selectedDataLayer = this.globalStore.selectedLayer();
    if (
      selectedDataLayer &&
      selectedDataLayer.parent === this.dataLayerType._id
    ) {
      return selectedDataLayer._id;
    } else {
      return (
        this.dataLayerStore.getLatestByParent(this.dataLayerType._id)?._id ??
        undefined
      );
    }
  }

  /**
   * Constructs and returns a router link configuration based on the presence of an initial data layer ID.
   *
   * If `this.initialDataLayerId` is present, the router link includes an additional outlet configuration for the initial data layer ID.
   * Otherwise, it only includes the outlet configuration for the data layer type ID.
   *
   * @return {Array} A router link configuration array, which includes the main route path and associated outlet settings.
   */
  getRouterLink() {
    return this.initialDataLayerId
      ? [
          '/data-backend',
          {
            outlets: {
              bearbeiten: [
                'ebene',
                this.dataLayerType?._id,
                {
                  outlets: {
                    jahr: [this.initialDataLayerId],
                  },
                },
              ],
            },
          },
        ]
      : [
          '/data-backend',
          {
            outlets: {
              bearbeiten: ['ebene', this.dataLayerType?._id],
            },
          },
        ];
  }

  /**
   * Retrieves an array of menu item objects, each containing labels, icons, and associated actions.
   *
   * @param {any} routerLink - A dynamic link parameter used for navigation.
   * @return {Array<Object>} An array of menu items, each with properties such as label, icon, routerLink, and command.
   */
  getMenuItems(routerLink: any) {
    return [
      {
        label: 'bearbeiten',
        icon: 'fas fa-pen',
        routerLink: routerLink,
        command: (event: MenuItemCommandEvent) => {
          console.log(event);
        },
      },
      {
        label: 'Neues Jahr hinzufügen',
        icon: 'fas fa-calendar-days',
        command: (event: MenuItemCommandEvent) => {
          this.addYear.emit(this.dataLayerType);
        },
      },
    ];
  }
}
